import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import api from "../../api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import {
  NEW_COMMENT,
  POET_AVALON_POE,
  USERGROUP_CODING,
  USERGROUP_CODING_REVIEW,
} from "../../constants/Constants";
import PolicyTypeBanner from "components/PolicyTypeBanner";
import FirstLineContainer from "components/common/FirstLineContainer";
import Drawers from "../../components/common/Drawers";
import GreyOutButton from "components/GreyOutButton/GreyOutButton";
import PolicyAdoption from "api/policyAdoption";
import { formErrorStrfromObj } from "utils/shared";
import { UserContext } from "context";

const TopPanelComponent = ({
  policyStateData,
  policyHeader,
  policyKey,
  healthPlanDisplayName,
  healthPlanKey,
  updatePolicyData,
  handleCancelAction,
  policyNumber,
  policyTitle,
  policyVersion,
  editable,
  showCodingReviewerError = false,
  onCodingReviewerChange,
}) => {
  const user = useContext(UserContext);
  const [coverageCriteria, setCoverageCriteria] = useState(
    policyHeader?.TotalCoverageCriteria
  );
  const [policyData, setPolicyData] = useState({
    policyTranslator: "",
    policyCoder: "",
    translationReviewer: "",
    codingStatus: "",
    codingReviewer: "",
    translationStatus: "",
    reviewType: "",
    overallAdoptionDecision: "",
    lobAdoptionDecisionList: [],
    policyChangeControlXrefDto: {
      policyChangeControlXrefKey: null,
      changeControlText: "",
    },
  });
  const [policyCoders, setPolicyCoders] = useState([]);
  const [codingReviewers, setCodingReviewers] = useState([]);

  const [activeDrawer, setActiveDrawer] = useState(null);
  const [policyHistoryData, setPolicyHistoryData] = useState([]);
  const [ctReviewData, setCTReviewData] = useState([]);
  const [reviewData, setReviewData] = useState({
    ctReviewKey: undefined,
    results: "",
    comments: "",
    reviewType: "",
  });

  const dispatch = useDispatch();
  const hasCtReviewEditRole =
    user?.groups?.includes("POET-Avalon-Admin") ||
    (user?.groups?.includes("POET-Avalon-POE") &&
      (user?.POETSkill?.includes("Coding") ||
        user?.POETSkill?.includes("Manager")));
  const isCtReviewEditableStatus =
    policyData.codingStatus === "Peer Review" ||
    policyData.codingStatus === "PDC Review";
  const ctReviewEditable =
    editable && hasCtReviewEditRole && isCtReviewEditableStatus;

  const handleCoverageCriteria = (event) => {
    const newValue = event.target.value.trim();
    if (/^\d{0,2}$/.test(newValue)) {
      if (
        newValue === "" ||
        (parseInt(newValue, 10) >= 0 && parseInt(newValue, 10) <= 99)
      ) {
        setCoverageCriteria(newValue);
      }
    }
  };

  const handlePolicyCoderChange = (event) => {
    setPolicyData({ ...policyData, policyCoder: event.target.value });
  };

  /**
   * Updates coding reviewer selection and notifies parent for inline validation state.
   *
   * @param {import('react').ChangeEvent<{ value: unknown }>} event
   * @returns {void}
   */
  const handleCodingReviewChange = (event) => {
    setPolicyData({ ...policyData, codingReviewer: event.target.value });
    onCodingReviewerChange?.(event.target.value);
  };

  const handleChangeControlChange = (event) => {
    setPolicyData((prevData) => ({
      ...prevData,
      policyChangeControlXrefDto: {
        ...prevData.policyChangeControlXrefDto,
        changeControlText: event.target.value.trim(),
      },
    }));
  };

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        dispatch(setLoading(true));
        const response = await api.policyData.getPolicyData(
          healthPlanKey,
          policyKey
        );

        if (response.status === 200 && response.data != null) {
          setPolicyData({
            policyTranslator: response.data.policyTranslator,
            policyCoder: response.data.policyCoder,
            translationReviewer: response.data.translationReviewer,
            codingStatus: response.data.codingStatus,
            codingReviewer: response.data.codingReviewer,
            translationStatus: response.data.translationStatus,
            reviewType: response.data.reviewType,
            overallAdoptionDecision: response.data.overallAdoptionDecision,
            lobAdoptionDecisionList: response.data.lobAdoptionDecisionList,
            policyChangeControlXrefDto:
              response.data.policyChangeControlXrefDto !== null
                ? response.data.policyChangeControlXrefDto
                : { policyChangeControlXrefKey: null, changeControlText: "" },
          });
          setPolicyHistoryData(response.data.policyHistoryList);
          setCTReviewData(response.data.policyCTReviewList);
        } else throw new Error("API failed to return policy data.");
      } catch (e) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(e), title: "Error" })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchPolicyData();
  }, []);

  //Populate drop downs
  useEffect(() => {
    const fetchUsers = async () => {
      dispatch(setLoading(true));

      try {
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();

        setPolicyCoders(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_CODING)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );

        setCodingReviewers(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_CODING_REVIEW)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );
      } catch (e) {
        dispatch(
          setDialog({
            message: e.message,
            title: "Error: Could not fetch users",
          })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    let description = "";
    description =
      policyData.codingStatus.toLowerCase() === "peer review"
        ? "Coding"
        : policyData.codingStatus.toLowerCase() === "pdc review"
          ? "PDC Coding"
          : "Coding";

    // Find the active/editable review row (the one the user needs to fill out):
    // 1. Prefer an unfinished row (completedDate null) matching the current stage reviewType.
    // 2. Fall back to any unfinished row in the list — handles cases where codingStatus maps
    //    to "PDC Coding" but existing review rows carry reviewType "Coding".
    // 3. Last resort: first row matching reviewType.
    const allRows = ctReviewData || [];
    const rowsForType = allRows.filter((row) => row.reviewType === description);
    const matchingRow =
      rowsForType.find((row) => !row.completedDate) ||
      allRows.find((row) => !row.completedDate) ||
      rowsForType[0];

    setReviewData((prev) => ({
      ...prev,
      // Use the matched row's reviewType so the payload reflects what the backend stored,
      // not the derived description which may differ (e.g. "Coding" vs "PDC Coding").
      reviewType: matchingRow?.reviewType || description,
      ctReviewKey: matchingRow?.ctReviewKey,
      results: matchingRow?.results || "",
      comments: matchingRow?.comments || "",
    }));
  }, [ctReviewData, policyData.codingStatus]);

  const handleDrawerButtonClick = (drawerType) => {
    setActiveDrawer(drawerType);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  /**
   * Submit policy for review, passing reviewData only if a C&T review key exists.
   * On first submit (Assigned/In Progress status), ctReviewKey is null so reviewData is not sent.
   * On subsequent submits after page reload, ctReviewKey is populated from API response.
   */
  const submitForReview = (submitForReview = true) => {
    const updateAssignessReviewerDto =
      !policyData.policyCoder && !policyData.codingReviewer
        ? null
        : {
            healthPlanKey: healthPlanKey,
            policyKey: policyKey,
            coding: policyData.policyCoder || null,
            codingReviewer: policyData.codingReviewer || null,
            policyStage: "Coding",
          };
    // Only pass reviewData if ctReviewKey exists; otherwise pass null
    const reviewDataToSend =
      reviewData?.ctReviewKey != null ? reviewData : null;
    updatePolicyData(
      submitForReview,
      policyHistoryData.filter((history) => history.type === NEW_COMMENT),
      reviewDataToSend,
      updateAssignessReviewerDto,
      policyData.policyChangeControlXrefDto
    );
  };

  const handleSaveAsDraft = () => submitForReview(false);
  const handleSubmitForReview = () => submitForReview(true);

  const disableAdoptionDetailsDrawer = () => {
    return policyData.overallAdoptionDecision === null;
  };
  const policyTypeBannerText =
    `${healthPlanDisplayName} - ${policyVersion}` +
    (policyData.overallAdoptionDecision
      ? ` - ${policyData.overallAdoptionDecision}`
      : "");

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Policy Coding:
          </Box>{" "}
          {policyNumber} - {policyTitle}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: "right", flex: 1 }}>
          {policyHeader.PolicyType}
        </Typography>
      </FirstLineContainer>

      <PolicyTypeBanner
        bannerText={policyTypeBannerText}
        handleCancel={handleCancelAction}
        handleDraft={handleSaveAsDraft}
        handleReview={handleSubmitForReview}
        editable={editable}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Left Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Review Type:
            </Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyStateData?.cowName} {policyStateData?.quarter}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Change Control:
            </Typography>
            <TextField
              fullWidth
              sx={{ flex: 1 }}
              value={policyData.policyChangeControlXrefDto?.changeControlText}
              onChange={(e) => handleChangeControlChange(e)}
            ></TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5, pt: "8px" }}
            >
              Coding Reviewer:
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              error={showCodingReviewerError}
              sx={{ flex: 1 }}
            >
              <Select
                value={policyData.codingReviewer}
                onChange={handleCodingReviewChange}
              >
                {codingReviewers.map((reviewer) => (
                  <MenuItem
                    key={reviewer.userKey}
                    value={reviewer.userEmailAddress}
                  >
                    {`${reviewer.userFirstName} ${reviewer.userLastName}`}
                  </MenuItem>
                ))}
              </Select>
              {showCodingReviewerError && (
                <FormHelperText
                  data-testid="coding-reviewer-required-error"
                  error
                  sx={{ mt: "4px" }}
                >
                  Coding Reviewer Required.
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        {/* Center Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Coding Status:
            </Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyData.codingStatus}
            </Typography>
          </Box>
          <FormControl
            fullWidth
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Policy Coder:
            </Typography>
            <Select
              sx={{ flex: 1 }}
              value={policyData.policyCoder}
              onChange={handlePolicyCoderChange}
            >
              {policyCoders.map((coder) => (
                <MenuItem key={coder.userKey} value={coder.userEmailAddress}>
                  {`${coder.userFirstName} ${coder.userLastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right Column */}
        <Box sx={{ mr: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <GreyOutButton
            variant="outlined"
            size="small"
            disabled={disableAdoptionDetailsDrawer()}
            onClick={() => handleDrawerButtonClick("adoptionDetails")}
          >
            Adoption Details
          </GreyOutButton>
          <GreyOutButton
            variant="outlined"
            size="small"
            onClick={() => handleDrawerButtonClick("ctReviews")}
          >
            C&T Reviews
          </GreyOutButton>
          <GreyOutButton
            size="small"
            variant="outlined"
            onClick={() => handleDrawerButtonClick("policyHistory")}
          >
            Policy History
          </GreyOutButton>
        </Box>
      </Box>
      {/* Use the Drawers component */}
      {activeDrawer && (
        <Drawers
          policyVersion={policyVersion}
          editable={editable}
          ctReviewEditable={ctReviewEditable}
          activeDrawer={activeDrawer}
          closeDrawer={closeDrawer}
          healthPlanKey={healthPlanKey}
          policyKey={policyKey}
          status={policyData.codingStatus}
          lobAdoptionDecisions={policyData.lobAdoptionDecisionList}
          policyHistoryData={policyHistoryData}
          setPolicyHistoryData={setPolicyHistoryData}
          ctReviewData={ctReviewData}
          reviewData={reviewData}
          setReviewData={setReviewData}
        />
      )}
    </>
  );
};

export default TopPanelComponent;
